import { Platform } from "react-native"

import { router } from "expo-router"

/**
 * Builds a channel deeplink URL in the form `<base_url>?channel=<channel_id>`.
 * On web the base URL defaults to the current origin with a trailing slash.
 * On native the custom scheme is used.
 */
export function buildChannelDeeplinkUrl(channelId: string, baseUrl?: string): string {
  const trimmed = channelId.trim()
  if (!trimmed) {
    throw new Error("buildChannelDeeplinkUrl: channelId is required")
  }

  if (Platform.OS === "web" && typeof window !== "undefined") {
    const url = new URL(baseUrl ?? `${window.location.origin}/`)
    url.search = ""
    url.hash = ""
    url.searchParams.set("channel", trimmed)
    return url.toString()
  }

  return `sidekick://?channel=${encodeURIComponent(trimmed)}`
}

/**
 * Navigates to the channel deeplink route and ensures the URL includes `?channel=<id>`.
 * Also persists the channel id via router so ChannelLinkCapture can pick it up.
 */
export function navigateToChannelDeeplink(channelId: string): void {
  const trimmed = channelId.trim()
  if (!trimmed) return

  const path = `/?channel=${encodeURIComponent(trimmed)}`

  if (Platform.OS === "web" && typeof window !== "undefined") {
    const targetUrl = buildChannelDeeplinkUrl(trimmed)
    window.history.replaceState(null, "", targetUrl)
  }

  router.replace(path as Parameters<typeof router.replace>[0])
}

/**
 * After logout, navigate directly to the unauth destination for this channel.
 * Avoids an intermediate hop through `/?channel=` (and the app layout redirect)
 * which causes a visible refresh on the branded login screen.
 */
export function navigateAfterChannelLogout(channelId: string, isMediaMode: boolean): void {
  const trimmed = channelId.trim()
  if (!trimmed) return

  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.history.replaceState(null, "", buildChannelDeeplinkUrl(trimmed))
  }

  if (isMediaMode !== false) {
    router.replace(`/unauth/channel-library/${trimmed}` as Parameters<typeof router.replace>[0])
    return
  }

  router.replace("/unauth/login")
}
